import { API_BASE_URL } from '../config/env';
import { getAuthToken } from './authStorage';

export interface Club {
  id: string;
  name: string;
  code: string;
  description?: string;
  is_public: boolean;
  total_members: number;
  total_tables: number;
  current_user_wallet_balance?: number;
  created_at?: string;
}

export type ClubPaymentIntentStatus =
  | 'PENDING'
  | 'AWAITING_PAYMENT'
  | 'CONFIRMED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED';

export type ClubWithdrawalPixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
export type ClubWithdrawalRequestStatus =
  | 'REQUESTED'
  | 'UNDER_REVIEW'
  | 'RESERVED'
  | 'PROCESSING'
  | 'PAID'
  | 'FAILED'
  | 'CANCELLED';

export interface ClubPaymentIntent {
  id: string;
  club_payment_account_id: string;
  club_id: string;
  user_id: string;
  requested_by_user_id: string | null;
  provider: 'ASAAS';
  type: 'DEPOSIT';
  status: ClubPaymentIntentStatus;
  billing_type: 'PIX';
  amount_reais: number;
  finance_entry_id: string | null;
  provider_payment_id: string | null;
  provider_customer_id: string | null;
  provider_invoice_url: string | null;
  provider_checkout_url: string | null;
  provider_qr_code_payload: string | null;
  provider_qr_code_image: string | null;
  idempotency_key: string | null;
  external_reference: string | null;
  description: string | null;
  metadata_json: Record<string, any>;
  requested_at: string;
  expires_at: string | null;
  confirmed_at: string | null;
  failed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClubWithdrawalDestination {
  id: string;
  club_id: string;
  user_id: string;
  pix_key_type: ClubWithdrawalPixKeyType;
  pix_key_masked: string;
  recipient_name: string;
  recipient_document_masked: string;
  changed_at: string;
  available_for_withdrawal_at: string;
  created_at: string;
  updated_at: string;
}

export interface ClubWithdrawalRequest {
  id: string;
  club_payment_account_id: string;
  club_id: string;
  user_id: string;
  requested_by_user_id: string | null;
  approved_by_user_id: string | null;
  processed_by_user_id: string | null;
  provider: 'ASAAS';
  status: ClubWithdrawalRequestStatus;
  amount_reais: number;
  finance_entry_id: string | null;
  provider_transfer_id: string | null;
  pix_key_snapshot: string | null;
  recipient_name_snapshot: string | null;
  recipient_document_snapshot: string | null;
  idempotency_key: string | null;
  external_reference: string | null;
  reason: string | null;
  failure_reason: string | null;
  metadata_json: Record<string, any>;
  requested_at: string;
  approved_at: string | null;
  paid_at: string | null;
  failed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  reservation_status: 'ACTIVE' | 'RELEASED' | 'CONSUMED' | 'CANCELLED' | null;
  reservation_released_at: string | null;
  reservation_consumed_at: string | null;
}

interface SuccessEnvelope<T> {
  success: boolean;
  data: T;
}

interface PaginatedResponse<T> {
  total: number;
  page: number;
  limit: number;
  data: T[];
}

class ClubService {
  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
    };
  }

  async listClubs(page = 1, limit = 24): Promise<PaginatedResponse<Club>> {
    const response = await fetch(`${API_BASE_URL}/clubs?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erro ao listar clubs: ${response.statusText}`);
    }

    return response.json();
  }

  async getClub(clubId: string): Promise<Club> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erro ao obter club: ${response.statusText}`);
    }

    return response.json();
  }

  async createDepositIntent(
    clubId: string,
    input: {
      amountReais: number;
      billingType?: 'PIX';
      description?: string;
      idempotencyKey?: string;
    }
  ): Promise<ClubPaymentIntent> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/payment-intents/deposit`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(input),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao criar deposito Pix');
    }

    return (payload as SuccessEnvelope<ClubPaymentIntent>).data;
  }

  async listMyDepositIntents(clubId: string, limit = 20): Promise<ClubPaymentIntent[]> {
    const response = await fetch(
      `${API_BASE_URL}/clubs/${clubId}/payment-intents/mine?limit=${encodeURIComponent(String(limit))}`,
      { headers: this.getHeaders() }
    );

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao carregar depositos do jogador');
    }

    return Array.isArray((payload as SuccessEnvelope<ClubPaymentIntent[]>).data)
      ? (payload as SuccessEnvelope<ClubPaymentIntent[]>).data
      : [];
  }

  async getDepositIntent(clubId: string, intentId: string): Promise<ClubPaymentIntent> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/payment-intents/${intentId}`, {
      headers: this.getHeaders(),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao carregar deposito');
    }

    return (payload as SuccessEnvelope<ClubPaymentIntent>).data;
  }

  async cancelDepositIntent(clubId: string, intentId: string): Promise<ClubPaymentIntent> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/payment-intents/${intentId}/cancel`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao cancelar deposito');
    }

    return (payload as SuccessEnvelope<ClubPaymentIntent>).data;
  }

  async getWithdrawalDestination(clubId: string): Promise<ClubWithdrawalDestination | null> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/withdrawal-destination`, {
      headers: this.getHeaders(),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao carregar destino Pix');
    }

    return ((payload as SuccessEnvelope<ClubWithdrawalDestination | null>).data || null) as ClubWithdrawalDestination | null;
  }

  async upsertWithdrawalDestination(
    clubId: string,
    input: {
      pixKeyType: ClubWithdrawalPixKeyType;
      pixKey: string;
      recipientName: string;
      recipientDocument: string;
      currentPassword: string;
    }
  ): Promise<ClubWithdrawalDestination> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/withdrawal-destination`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(input),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao salvar destino Pix');
    }

    return (payload as SuccessEnvelope<ClubWithdrawalDestination>).data;
  }

  async listMyWithdrawalRequests(clubId: string, limit = 20): Promise<ClubWithdrawalRequest[]> {
    const response = await fetch(
      `${API_BASE_URL}/clubs/${clubId}/withdrawal-requests/mine?limit=${encodeURIComponent(String(limit))}`,
      { headers: this.getHeaders() }
    );

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao carregar saques do jogador');
    }

    return Array.isArray((payload as SuccessEnvelope<ClubWithdrawalRequest[]>).data)
      ? (payload as SuccessEnvelope<ClubWithdrawalRequest[]>).data
      : [];
  }

  async createWithdrawalRequest(
    clubId: string,
    input: {
      amountReais: number;
      currentPassword: string;
      reason?: string;
    }
  ): Promise<ClubWithdrawalRequest> {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/withdrawal-requests`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(input),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao criar solicitacao de saque');
    }

    return (payload as SuccessEnvelope<ClubWithdrawalRequest>).data;
  }

  async cancelWithdrawalRequest(clubId: string, withdrawalRequestId: string): Promise<ClubWithdrawalRequest> {
    const response = await fetch(
      `${API_BASE_URL}/clubs/${clubId}/withdrawal-requests/${withdrawalRequestId}/cancel`,
      {
        method: 'POST',
        headers: this.getHeaders(),
      }
    );

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Erro ao cancelar solicitacao de saque');
    }

    return (payload as SuccessEnvelope<ClubWithdrawalRequest>).data;
  }
}

export const clubService = new ClubService();
